<script lang="ts">
	import type { EmblaCarouselType } from 'embla-carousel';
	import emblaCarouselSvelte from 'embla-carousel-svelte';
	import { onDestroy } from 'svelte';
	import { LatestReleasesCards } from './LatestReleasesCards';

	import { HorizontalScrollControls } from '#lib/components/HorizontalScrollControls/index.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { emblaScrollHint } from '#lib/utils/emblaScrollHint/index.js';
	import {
		initialHorizontalScrollState,
		type HorizontalScrollState,
	} from '#lib/utils/horizontalScrollHint/index.js';
	import { EditionsRoutes } from '#lib/websites/editions/constants/routes.js';
	import { PrimaryRoutes } from '#lib/websites/primary/constants/routes.js';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { Link } from '$global/components/Link';
	import { LinkButton } from '$global/components/LinkButton';
	import { Txt } from '$global/components/Txt';

	let { liveDropInfoCtx } = getLiveDropInfoContext();
	let liveDropInfo = $derived(liveDropInfoCtx.liveDropInfo);
	let releasesCarouselViewport: HTMLDivElement | undefined = $state();
	let releasesEmblaApi: EmblaCarouselType | undefined = $state();
	let releasesCarouselState: HorizontalScrollState = $state(
		initialHorizontalScrollState
	);
	let destroyReleasesScrollHint: (() => void) | undefined;

	const handleReleasesCarouselInit = (
		event: CustomEvent<EmblaCarouselType>
	) => {
		destroyReleasesScrollHint?.();
		releasesEmblaApi = event.detail;

		if (releasesCarouselViewport) {
			destroyReleasesScrollHint = emblaScrollHint(
				releasesCarouselViewport,
				releasesEmblaApi,
				(state) => (releasesCarouselState = state)
			);
		}
	};

	onDestroy(() => destroyReleasesScrollHint?.());
</script>

<section>
	<div class="mb-5 flex items-end justify-between gap-4">
		<div>
			<Txt variant="h3" component="h2" class="absolute mb-1 whitespace-nowrap"
				>Latest artwork releases</Txt
			>
			<Txt variant="body3" class="pt-10 text-gray-500">
				Recent and current releases from HENI Editions and HENI Primary.
			</Txt>
		</div>

		<div class="flex shrink-0 items-center gap-3">
			<HorizontalScrollControls
				emblaApi={releasesEmblaApi}
				state={releasesCarouselState}
				variant="inline"
				previousLabel="Show previous artwork releases"
				nextLabel="Show more artwork releases"
				class="lg:hidden"
			/>

			<LinkButton
				href={GlobalRoutes.ArtworksHome}
				dataCy="global-home-explore-works-top"
				size="md"
				class="hidden shrink-0 sm:block"
			>
				Explore all artworks
				{#snippet trailing()}<ChevronRightIcon />{/snippet}
			</LinkButton>
		</div>
	</div>

	<div
		class="mb-4 hidden h-auto w-full grid-flow-row grid-cols-4 justify-center gap-6 lg:grid"
	>
		<LatestReleasesCards {liveDropInfo} />
	</div>

	<div
		bind:this={releasesCarouselViewport}
		use:emblaCarouselSvelte={{
			options: {
				align: 'start',
				containScroll: 'trimSnaps',
			},
			plugins: [],
		}}
		onemblaInit={handleReleasesCarouselInit}
		class="content-carousel-hint mb-6 overflow-hidden lg:hidden"
		role="region"
		aria-roledescription="carousel"
		aria-label="Latest artwork releases"
	>
		<div class="flex gap-6">
			<LatestReleasesCards
				{liveDropInfo}
				classes={{
					card: 'min-w-0 flex-[0_0_82%] sm:flex-[0_0_47%] md:flex-[0_0_31%]',
				}}
			/>
		</div>
	</div>

	<LinkButton
		href={GlobalRoutes.ArtworksHome}
		dataCy="global-home-explore-works-bottom"
		size="md"
		fullWidth
		class="mb-5 block sm:hidden"
	>
		Explore all artworks
		{#snippet trailing()}<ChevronRightIcon />{/snippet}
	</LinkButton>

	<div class="flex flex-wrap gap-x-7 gap-y-3">
		<Link
			href={EditionsRoutes.Gallery}
			dataCy="global-home-browse-editions"
			class="group"
		>
			<Txt variant="label3" class="underline group-hover:text-gray-600">
				Browse all Editions &rarr;
			</Txt>
		</Link>
		<Link
			href={PrimaryRoutes.Releases}
			dataCy="global-home-browse-primary"
			class="group"
		>
			<Txt variant="label3" class="underline group-hover:text-gray-600">
				Browse all Primary releases &rarr;
			</Txt>
		</Link>
	</div>
</section>
